<template>
    <div class="flex flex-col h-full gap-2 px-2">
        <DxTreeList
            class="h-0 grow w-full"
            id="tree-list"
            ref="listRef"
            :data-source="dataSource"
            :columns="listColumns"
            :show-row-lines="true"
            :show-borders="true"
            :column-auto-width="true"
            :root-value="null"
            :allow-column-reordering="true"
            :expand-nodes-on-filtering="false"
            :column-resizing-mode="'widget'"
            :allow-column-resizing="true"
            dataStructure="plain"
            :filteringMode="'remote'"
            :key-expr="keyExpr"
            has-items-expr="has_items"
            filterMode="fullBranch"
            :focused-row-enabled="true"
            :parent-id-expr="parentIdExpr"
            :filter-row="{ visible: true }"
            :filter-panel="{ visible: true }"
            :remote-operations="{ filtering: true, paging: true, sorting: true }"
            :scrolling="{
                mode: 'standart',
                scrollByContent: true,
                scrollByThumb: true,
            }"
            :paging="{ enabled: false }"
            @editor-preparing="editorPreparing"
            @selectionChanged="selectionChanged"
            @focusedRowChanging="onFocusedRowChanging"
            @option-changed="onOptionChangedFunc"
            @rowExpanded="onRowExpandedFunc"
            @contentReady="onContentReady"
            @context-menu-preparing="onContextMenu"
            @exporting="onExporting"
        >
            <dx-toolbar>
                <slot name="toolbarItems" />

                <dx-item
                    :visible="props.toolbarMeta?.refreshButton?.visible !== false"
                    widget="dxButton"
                    location="after"
                    name="refresh"
                >
                    <dx-button hint="Обновить" icon="refresh" @click="refresh()" />
                </dx-item>

                <dx-item
                    :visible="props.toolbarMeta?.editButton?.visible !== false"
                    location="after"
                    widget="dxButton"
                    name="edit"
                >
                    <dx-button hint="Редактировать" icon="edit" @click="onEditClick()" />
                </dx-item>

                <dx-item
                    :visible="props.toolbarMeta?.addButton?.visible !== false"
                    location="after"
                    widget="dxButton"
                    name="add"
                >
                    <dx-button hint="Добавить" icon="add" @click="onAddClick()" />
                </dx-item>

                <dx-item
                    :visible="props.toolbarMeta?.deleteButton?.visible !== false"
                    location="after"
                    widget="dxButton"
                    name="delete"
                >
                    <dx-button
                        hint="Удалить запись"
                        icon="trash"
                        @click="onDeleteClick()"
                        :disabled="isDeleteButtonDisabled"
                    />
                </dx-item>

                <dx-item
                    :visible="props.toolbarMeta?.copyButton?.visible !== false"
                    location="after"
                    widget="dxButton"
                    name="copy"
                >
                    <dx-button hint="Копировать" icon="copy" @click="onCopyClick()" />
                </dx-item>

                <dx-item
                    :visible="props.toolbarMeta?.saveButton?.visible !== false"
                    location="after"
                    widget="dxButton"
                    name="save"
                >
                    <dx-button
                        hint="Сохранить изменения"
                        icon="save"
                        @click="() => listInstance.value.saveEditData()"
                    />
                </dx-item>

                <dx-item name="exportButton" location="after" locate-in-menu="auto" />

                <dx-item
                    :visible="props.toolbarMeta?.menuButton?.visible !== false"
                    location="after"
                    widget="dxMenu"
                    name="menu"
                    class="settings"
                    locateInMenu="auto"
                    :options="menuOptions"
                >
                </dx-item>

                <dx-item
                    :visible="props.toolbarMeta?.columnChooserButton?.visible !== false"
                    location="after"
                    name="columnChooserButton"
                />

                <dx-item
                    :visible="props.toolbarMeta?.searchButton?.visible !== false"
                    location="after"
                    name="searchPanel"
                />
            </dx-toolbar>
            <dx-editing
                :allow-adding="false"
                :allow-deleting="false"
                :allow-updating="false"
                :use-icons="true"
                mode="batch"
            />

            <DxFilterBuilder :group-operations="['and', 'notAnd']" />
            <DxFilterRow :visible="true" />
            <DxSearchPanel :visible="true" :searchEnabled="true" placeholder="Поиск..." />
            <DxFilterPanel :visible="true" />
            <DxSelection mode="multiple" :allowSelectAll="false" :recursive="true" />
            <DxRemoteOperations :group-paging="true" :filtering="true" />
            <DxColumnChooser :enabled="true" :width="300" :height="500" mode="select">
                <DxPosition my="left top" at="left bottom" of=".dx-icon-preferences" />
            </DxColumnChooser>
            <DxSorting ref="sortRef" mode="multiple" />
        </DxTreeList>
        <grid-footer ref="childRef" :totalCount="treeListData.totalCount"></grid-footer>
    </div>
</template>

<script>
export default {
    name: 'tree-list-card',
};
</script>

<script setup>
import {
    DxTreeList,
    DxFilterRow,
    DxSearchPanel,
    DxFilterBuilder,
    DxColumnChooser,
    DxPosition,
    DxFilterPanel,
    DxSelection,
    DxRemoteOperations,
    DxToolbar,
    DxItem,
    DxSorting,
    DxEditing,
} from 'devextreme-vue/tree-list';
import { DxButton } from 'devextreme-vue';
import { computed, onDeactivated, onMounted, ref, watch } from 'vue';
import DataSource from 'devextreme/data/data_source';
import { useRouter } from 'vue-router';
import store from '@/store';
import { getCookie } from '@/utils/cookies';
import { treeListStore } from './tree-list-card-store';
import gridFooter from '../grid-footer.vue';
import onExporting from '@/utils/export-to-excel.js';

const props = defineProps({
    keyExpr: {
        type: String,
        default: 'id',
    },
    parentIdExpr: {
        type: String,
        default: 'parent_id',
    },
    toolbarMeta: {
        type: Object,
        default: () => {},
    },
    contextMenuMeta: {
        type: Array,
        default: () => [],
    },
    cardName: {
        type: String,
        default: () => '',
    },
    customContextMenu: {
        type: Array,
        default: () => [],
    },
    dbSchema: {
        type: String,
        default: '',
    },
    dbSource: {
        type: String,
        default: '',
    },
    api: {
        type: String,
        default: '',
    },
    viewName: {
        type: String,
        default: '',
    },
});

const listInstance = ref(null);
const listRef = ref(null);
const listColumns = ref([]);
const cellTemplates = [];
const editCellTemplates = [];
const filterOperations = {
    string: ['contains', 'notcontains', 'startswith', 'endswith', '=', '<>'],
    numeric: ['=', '<>', '<', '>', '<=', '>='],
    date: ['=', 'between'],
};
const router = useRouter();
const sortRef = ref(null);
const scrolled = ref(0);
const childRef = ref(null);
const rowsPreparedToDelete = ref([]);
const isDeleteButtonDisabled = ref(true);

const treeListData = ref({
    data: [],
    totalCount: 0,
});

const menuOptions = {
    items: [
        {
            icon: 'preferences',
            items: [
                {
                    visible: true,
                    name: 'defaultSettings',
                    text: 'Заводские настройки',
                    onClick: () => {},
                },
                {
                    visible: true,
                    name: 'userSettings',
                    text: 'Настройки пользователя',
                    onClick: async () => {
                        listRef.value.instance.refreshMeta();
                    },
                },
            ],
            alignment: 'center',
        },
    ],
};

const loadData = async loadOptions => {
    let initArgs = {
        db_schema_name: props.dbSchema,
        db_view_name: props.dbSource,
        api: props.api,
        token: getCookie('token'),
    };
    treeListData.value = await treeListStore(
        listRef.value.instance,
        loadOptions,
        treeListData.value.data,
        initArgs,
        treeListData.value.totalCount
    );
};

const onContextMenu = event => {
    if (event.target !== 'content') return;
    let items = [
        {
            name: 'gotoCard',
            text: 'Посмотреть карточку',
            visible: true,
            onItemClick() {
                const id = event.row.data[props.keyExpr];
                router.push({
                    name: props.cardName,
                    params: { id },
                });
            },
        },
    ];
    event.items = items;
    if (event.row && props.customContextMenu) {
        const context = props.customContextMenu.items?.map(item => {
            return { ...item, rowData: event.row };
        });
        context && context.length > 0 && event.items.push(...context);
    }
    event.items = recursivelyChangeProperty(event.items, props.contextMenuMeta, 'name');
};

const onContentReady = e => {
    rowsPreparedToDelete.value.forEach(row => {
        const node = listInstance.value.getNodeByKey(row);
        if (node?.children?.length > 0 && listInstance.value.isRowExpanded(row)) {
            node.children.forEach(child => {
                if (!rowsPreparedToDelete.value.includes(child.key)) {
                    deleteRow(child.key);
                }
            });
        }
    });

    setTimeout(() => {
        let scroll = e.component.getScrollable();
        scroll.on('scroll', e => {
            setTimeout(() => {
                if (e.reachedBottom && scrolled.value < e.scrollOffset.top) {
                    scrollCount.value++;
                    const loadOptions = {
                        mode: 'onScroll',
                        skip: skip.value,
                    };
                    loadData(loadOptions);
                }
                scrolled.value = e.scrollOffset.top;
            }, 100);
        });
    });
};

const dataSource = new DataSource({
    store: {
        data: treeListData.value.data,
        key: 'id',
        type: 'array',
    },
});

const onFocusedRowChanging = e => {
    emit('onFocusedRowChanging', e);
};

const onRowExpandedFunc = async e => {
    const node = listInstance.value.getNodeByKey(e.key);
    if (node?.children?.length > 0 && !rowsPreparedToDelete.value.includes(node.key)) {
        node.children.forEach(child => {
            if (rowsPreparedToDelete.value.includes(child.key)) {
                undeleteRow(child.key);
            }
        });
    }
    if (typeof e.key !== 'number') {
        const expanded = e.component
            .option('expandedRowKeys')
            .filter(el => typeof e.key === 'string' || (typeof el === 'string' && e.key !== el.toString()));
        e.component.option('expandedRowKeys', expanded);
        const loadOptions = {
            mode: 'onClick',
            skip: skip.value,
        };
        await loadData(loadOptions);
    }
};

const processColumns = (columns, parentId) => {
    return columns
        .filter(column => column.parent_id === parentId)
        .sort((a, b) => a.index - b.index)
        .map(column => {
            const childColumns = processColumns(columns, column.id);
            if (childColumns.length > 0) {
                const obj = {
                    caption: column.text,
                    columns: childColumns,
                };
                if (column.cssClass) obj.cssClass = column.cssClass;
                return obj;
            } else {
                const obj = {
                    caption: column.text,
                    visible: column.visible !== false,
                };
                obj.dataField = column.field ? column.field : '';
                obj.dataType = column.data_type ? column.data_type : '';
                obj.filterOperations = column.data_type ? filterOperations[column.data_type] : '';
                obj.width = column.width ? column.width : 100;
                if (column.format) obj.format = column.format;
                if (column.cellTemplate) {
                    const name = `cellTemplate-${column.field}`;
                    const template = { name, component: column.cellTemplate };
                    cellTemplates.push(template);
                    obj.cellTemplate = name;
                }
                if (column.editCellTemplate) {
                    const name = `editCellTemplate-${column.field}`;
                    const template = { name, component: column.editCellTemplate };
                    editCellTemplates.push(template);
                    obj.editCellTemplate = name;
                }
                if (column.field === 'id') obj.allowFiltering = false;
                if (column.editorOptions) obj.editorOptions = column.editorOptions;
                if (column.sortingMethod) obj.sortingMethod = column.sortingMethod;
                if (column.alignment) obj.alignment = column.alignment;
                if (column.fixed) obj.fixed = column.fixed;
                if (column.cssClass) obj.cssClass = column.cssClass;
                return obj;
            }
        });
};

const onOptionChangedFunc = async e => {
    if (e.name === 'searchPanel') {
        scrollCount.value = 0;
        const loadOptions = {
            mode: 'onFilter',
            skip: skip.value,
        };
        await loadData(loadOptions);
        e.component.getScrollable().scrollTo(0);
    }
    if (e.name === 'filterValue' || e.fullName.includes('sort')) {
        let sort;
        // парсинг текущего значения сортировки
        if (e.fullName.includes('sort')) {
            sort = [];
            const columnCount = +e.fullName.split('[')[1].split('')[0];
            var columns = e.component.state().columns;
            const column = columns[columnCount];
            const currentSortValue = {
                selector: column.name,
                desc: e.value === 'desc',
            };
            sort.push(currentSortValue);
        }

        scrollCount.value = 0;
        const loadOptions = {
            mode: 'onFilter',
            skip: skip.value,
            sort: sort,
        };
        await loadData(loadOptions);
        e.component.getScrollable().scrollTo(0);
        e.component.option('focusedRowIndex', -1);
        e.component.forEachNode(node => {
            e.component.collapseRow(node.key);
        });
    }
};

const editorPreparing = async e => {
    e.updateValueTimeout = 2000;
    if (e.parentType === 'searchPanel') {
        e.editorOptions.onValueChanged = function (arg) {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            this.searchTimeout = setTimeout(() => {
                if (arg.value.length == 0 || arg.value.length > 2) {
                    e.component.searchByText(arg.value);
                }
            }, 2000);
        };
    }
};

const selectionChanged = event => {
    emit('selectionChanged', event);
    childRef.value.selectRows(event.selectedRowKeys.length, treeListData.value.totalCount);
    event.selectedRowKeys.length > 0 ? (isDeleteButtonDisabled.value = false) : (isDeleteButtonDisabled.value = true);
};

const scrollCount = ref(0);

const skip = computed(() => {
    return scrollCount.value * 20;
});

const recursivelyChangeProperty = (toolbar, toolbarMeta, propertyName) => {
    toolbar.forEach(obj1 => {
        toolbarMeta.forEach(obj2 => {
            if (obj1.name === obj2.name) {
                obj1.visible = obj2.visible;
            }
        });
        if (Array.isArray(obj1.children)) {
            recursivelyChangeProperty(obj1.children, toolbarMeta, propertyName);
        }
    });
    return toolbar;
};

onDeactivated(() => {
    store.dispatch('cleanTreeListMetaData');
});

const loadMeta = async () => {
    if (props.api) await store.dispatch('loadTreeListMetaData', { name: props.viewName, api: props.api.split('/')[0] });
};

watch(treeListData, () => {
    if (listRef.value.instance) {
        listRef.value.instance.option('dataSource', treeListData.value.data);
        listRef.value.instance.repaintRows();
    }
});

const refresh = async => {
    treeListData.value.data = [];
    const loadOptions = {
        mode: 'onScroll',
        skip: 0,
    };
    loadData(loadOptions);
};

const onEditClick = () => {
    const id = listInstance.value.option('focusedRowKey');
    id &&
        router.push({
            name: props.cardName,
            params: { id },
        });
};

const onAddClick = () => {
    router.push({
        name: props.cardName,
    });
};
const onDeleteClick = () => {
    const selectedRows = listInstance.value.getSelectedRowKeys('');

    selectedRows.forEach(key => {
        const node = listInstance.value.getNodeByKey(key);
        if (rowsPreparedToDelete.value.includes(key)) {
            undeleteRow(key);
            if (listInstance.value.isRowExpanded(key)) {
                node?.children?.forEach(child => {
                    undeleteRow(child.key);
                });
            }
        } else {
            deleteRow(key);
            if (listInstance.value.isRowExpanded(key)) {
                node?.children?.forEach(child => {
                    deleteRow(child.key);
                });
            }
        }
    });
    listInstance.value.clearSelection();
};

const deleteRow = key => {
    const index = listInstance.value.getRowIndexByKey(key);
    listInstance.value.deleteRow(index);
    rowsPreparedToDelete.value.push(key);
};

const undeleteRow = key => {
    const index = listInstance.value.getRowIndexByKey(key);
    listInstance.value.undeleteRow(index);
    rowsPreparedToDelete.value = rowsPreparedToDelete.value.filter(value => value !== key);
};

const onCopyClick = () => {};

onMounted(async () => {
    await loadMeta();
    const loadOptions = {
        mode: 'onScroll',
        skip: 0,
    };

    await loadData(loadOptions);
    listInstance.value = listRef.value.instance;
    listColumns.value = processColumns(store.state.registryTreeList.regGrid, null);
});
const refreshMeta = () => {
    listColumns.value = processColumns(store.state.registryTreeList.regGrid, null);
};

defineExpose({
    listInstance,
    refreshMeta,
    refresh,
});

const emit = defineEmits([
    'expandParentItem',
    'getHiddenColumns',
    'onFocusedRowChanging',
    'selectionChanged',
    'onRowExpanding',
    'refresh',
]);
</script>

<style scoped>
:deep(.dx-menu-item-content) {
    display: flex;
    align-items: center;
}
:deep(.dx-row-removed > td) {
    background-color: white;
    text-decoration: line-through;
}
</style>
